import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import api from "../../api";
import { Modal } from "react-bootstrap";
import { notify } from "../../utils/toast";
import { Pagination, Stack } from "@mui/material";
import logo from "../../assets/images/logo.png";

const LIMIT = 10;

export const Salary = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showSalary, setShowSalary] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [editSalary, setEditSalary] = useState(null);

  const handleCloseSalary = () => setShowSalary(false);
  const handleShowSalary = () => setShowSalary(true);

  const openReceipt = (item) => {
    setSelectedSalary(item);
    setShowReceiptModal(true);
  };
  const closeReceipt = () => {
    setSelectedSalary(null);
    setShowReceiptModal(false);
  };

  const openEdit = (item) => setEditSalary(item);
  const closeEdit = () => setEditSalary(null);

  const getData = async (pageVal = page) => {
    try {
      setLoader(true);
      const response = await api.getSalary({ page: pageVal, limit: LIMIT });
      if (response?.success) {
        setData(response?.salaries || []);
        setTotal(response?.total || 0);
        setTotalPages(response?.totalPages || 1);
      }
    } catch (error) {
      notify.error("Something went wrong!");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getData(1);
  }, []);

  const handlePageChange = (e, value) => {
    setPage(value);
    getData(value);
  };

  const deleteSalary = async (id) => {
    if (!window.confirm("Are you sure you want to delete this salary record?")) return;
    try {
      const response = await api.deleteSalary(id);
      if (response?.success) {
        notify.success("Salary deleted successfully!");
        getData(page);
      }
    } catch (error) {
      notify.error("Something went wrong!");
    }
  };

  const updateSalaryStatus = async (id) => {
    try {
      const response = await api.updateSalary(id, { status: "paid" });
      if (response?._id) {
        notify.success("Salary marked as paid!");
        getData(page);
      }
    } catch (error) {
      notify.error("Something went wrong!");
    }
  };

  return (
    <>
      <div className="DashboardMain">
        <div className="row">
          <div className="col">
            <h3>Salaries ({total})</h3>

            {loader ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" />
              </div>
            ) : (
              <Table
                data={data}
                deleteSalary={deleteSalary}
                updateSalaryStatus={updateSalaryStatus}
                openReceipt={openReceipt}
                openEdit={openEdit}
              />
            )}

            {data && data.length > 0 && total > LIMIT && (
              <div className="pagination-container">
                <Stack spacing={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: "#10b981",
                        color: "white",
                        "&:hover": { backgroundColor: "#059669" },
                      },
                      "& .MuiPaginationItem-root": {
                        "&:hover": {
                          backgroundColor: "#d1fae5",
                          color: "#059669",
                        },
                      },
                    }}
                  />
                </Stack>
              </div>
            )}
          </div>
        </div>

        <div className="AddEmpBtn" onClick={handleShowSalary}>
          <i className="fa-solid fa-plus"></i>
        </div>
      </div>

      <GenerateSalaryModal
        show={showSalary}
        handleClose={handleCloseSalary}
        setLoader={setLoader}
        getData={() => getData(page)}
      />

      {selectedSalary && showReceiptModal && (
        <SalaryReceiptModal
          show={showReceiptModal}
          handleClose={closeReceipt}
          salary={selectedSalary}
        />
      )}

      {editSalary && (
        <EditSalaryModal
          show={!!editSalary}
          handleClose={closeEdit}
          salary={editSalary}
          getData={() => getData(page)}
        />
      )}
    </>
  );
};

const Table = ({ data, deleteSalary, updateSalaryStatus, openReceipt, openEdit }) => {
  const formatDate = (date) => moment(date).format("MMM Do YYYY, h:mm a");

  return (
    <div className="TableMain mt-3">
      <div className="table-responsive">
        <table className="table table-hover drivers-table">
          <thead>
            <tr>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Employee / Driver</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Month</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Base Salary</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Deductions</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Final Salary</th>
              <th scope="col">Status</th>
              <th scope="col" style={{ whiteSpace: "nowrap" }}>Created At</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, key) => (
                <tr key={key}>
                  <td>
                    <div className="table-text-dark w-max-c db" style={{ textTransform: "capitalize" }}>
                      {item?.driverId?.name || "—"}
                    </div>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db" style={{ fontWeight: 600 }}>
                      {item?.month || "—"}
                    </span>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db">
                      Rs. {(item?.baseSalary || 0).toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db" style={{ color: item?.deductions > 0 ? "#991b1b" : "#374151" }}>
                      {item?.deductions > 0 ? `- Rs. ${item.deductions.toLocaleString()}` : "—"}
                    </span>
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db" style={{ fontWeight: 700, color: "#065f46" }}>
                      Rs. {(item?.finalSalary || 0).toLocaleString()}
                    </span>
                  </td>
                  <td>
                    {item?.status === "paid" ? (
                      <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: "#d1fae5", color: "#065f46" }}>
                        Paid
                      </span>
                    ) : (
                      <button
                        style={{ background: "#fff", color: "#065f46", border: "1.5px solid #10b981", borderRadius: 7, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                        onClick={() => updateSalaryStatus(item?._id)}
                      >
                        <i className="fa-solid fa-check" style={{ marginRight: 5 }} />Mark as Paid
                      </button>
                    )}
                  </td>
                  <td>
                    <span className="table-text-dark w-max-c db" style={{ fontSize: 13 }}>{formatDate(item?.createdAt)}</span>
                  </td>
                  <td>
                    <div className="d-flex gap-2 align-items-center">
                      <button
                        style={{ background: "#ede9fe", color: "#6d28d9", border: "none", borderRadius: 7, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                        onClick={() => openReceipt(item)}
                      >
                        <i className="fa-solid fa-receipt" style={{ marginRight: 5 }} />Receipt
                      </button>
                      <div className="p-2 cursor-pointer" onClick={() => openEdit(item)}>
                        <i className="fa-solid fa-pen-to-square"></i>
                      </div>
                      <div className="delete p-2 cursor-pointer" onClick={() => deleteSalary(item?._id)}>
                        <i className="fa-solid fa-trash"></i>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-4 text-muted">
                  No salary records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const GenerateSalaryModal = ({ show, handleClose, setLoader, getData }) => {
  const [drivers, setDrivers] = useState([]);
  const [data, setData] = useState({});
  const [outstandingBalance, setOutstandingBalance] = useState(0);
  const [loadingLoans, setLoadingLoans] = useState(false);

  const fetchOutstanding = async (driverId) => {
    if (!driverId) {
      setOutstandingBalance(0);
      setData(d => ({ ...d, deductionAmount: "" }));
      return;
    }
    try {
      setLoadingLoans(true);
      // fetch all unpaid loans for this driver (no pagination limit needed here)
      const response = await api.getLoan({ driverId, status: "unpaid", limit: 1000, page: 1 });
      if (response?.success) {
        const loans = response?.loans || [];
        // remaining = original amount minus what has already been recovered
        const total = loans.reduce((sum, l) => sum + (l.amount - (l.paidAmount || 0)), 0);
        setOutstandingBalance(total);
        setData(d => ({ ...d, deductionAmount: total }));
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingLoans(false);
    }
  };

  const onTextChange = (e) => {
    const updated = { ...data, [e.target.name]: e.target.value };
    setData(updated);
    if (e.target.name === "driverId") fetchOutstanding(e.target.value);
  };

  const handleSubmit = async () => {
    if (!data?.driverId || !data?.month) {
      notify.error("Please select a driver and month");
      return;
    }
    const deductionAmount = Number(data?.deductionAmount ?? 0);
    if (deductionAmount < 0) {
      notify.error("Deduction amount cannot be negative");
      return;
    }
    if (deductionAmount > outstandingBalance) {
      notify.error(`Deduction (Rs. ${deductionAmount.toLocaleString()}) exceeds outstanding balance (Rs. ${outstandingBalance.toLocaleString()})`);
      return;
    }
    try {
      const payload = { driverId: data.driverId, month: data.month, deductionAmount };
      const response = await api.generateSalary(payload);
      if (response?.success) {
        notify.success("Salary successfully generated!");
        getData();
        setData({});
        setOutstandingBalance(0);
        handleClose();
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong");
    }
  };

  const getDriverData = async () => {
    try {
      setLoader(true);
      const response = await api.getDrivers();
      if (response?.success) setDrivers(response?.drivers || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => { getDriverData(); }, []);

  const deductionNum = Number(data?.deductionAmount ?? 0);
  const remainingAfter = outstandingBalance - deductionNum;

  return (
    <Modal show={show} onHide={handleClose}>
      <div className="taskForm">
        <div className="closeIcon" onClick={handleClose}>
          <i className="fa-solid fa-xmark" />
        </div>
        <div className="h3">Generate Salary</div>
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <label>Select Month</label>
              <input type="month" name="month" className="form-control" value={data?.month || ""} onChange={onTextChange} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Select Employee</label>
              <select name="driverId" className="form-control" value={data?.driverId || ""} onChange={onTextChange}>
                <option value="">Select</option>
                {drivers.map((item, key) => <option value={item?._id} key={key}>{item?.name}</option>)}
              </select>
            </div>
          </div>

          {data?.driverId && (
            loadingLoans ? (
              <div className="col-12 text-center py-2">
                <div className="spinner-border spinner-border-sm" role="status" />
              </div>
            ) : (
              <>
                {/* Outstanding balance info box */}
                <div className="col-12">
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    background: outstandingBalance > 0 ? "#fef2f2" : "#f0fdf4",
                    border: `1px solid ${outstandingBalance > 0 ? "#fca5a5" : "#86efac"}`,
                    borderRadius: 8, padding: "10px 14px", marginBottom: 12,
                  }}>
                    <span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>Outstanding Loan Balance</span>
                    <span style={{ fontWeight: 700, fontSize: 15, color: outstandingBalance > 0 ? "#991b1b" : "#15803d" }}>
                      Rs. {outstandingBalance.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Deduction amount input */}
                <div className="col-12">
                  <div className="form-group">
                    <label>
                      Deduction This Month
                      {outstandingBalance > 0 && (
                        <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 400, marginLeft: 6 }}>
                          (max Rs. {outstandingBalance.toLocaleString()})
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      name="deductionAmount"
                      className="form-control"
                      value={data?.deductionAmount ?? ""}
                      onChange={onTextChange}
                      min={0}
                      max={outstandingBalance}
                      disabled={outstandingBalance === 0}
                      placeholder={outstandingBalance === 0 ? "No outstanding loans" : `0 – ${outstandingBalance}`}
                    />
                    {outstandingBalance === 0 && (
                      <small style={{ color: "#15803d" }}>No loan deduction will be applied.</small>
                    )}
                  </div>
                </div>

                {/* Live preview of remaining balance */}
                {outstandingBalance > 0 && deductionNum > 0 && (
                  <div className="col-12">
                    <div style={{
                      background: "#f8fafc", border: "1px solid #e2e8f0",
                      borderRadius: 8, padding: "10px 14px", marginBottom: 4, fontSize: 13,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ color: "#374151" }}>Deducting now</span>
                        <span style={{ fontWeight: 700, color: "#991b1b" }}>- Rs. {deductionNum.toLocaleString()}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "#374151" }}>Remaining loan after this</span>
                        <span style={{ fontWeight: 700, color: remainingAfter > 0 ? "#b45309" : "#15803d" }}>
                          Rs. {remainingAfter.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )
          )}
        </div>

        <div className="mt-4">
          <button className="themeBtn w-100 text-center" onClick={handleSubmit}>Generate</button>
        </div>
      </div>
    </Modal>
  );
};

/* ─── Edit Salary Modal ─── */
const EditSalaryModal = ({ show, handleClose, salary, getData }) => {
  const [data, setData] = useState({
    month: salary?.month || "",
    baseSalary: salary?.baseSalary || 0,
    deductions: salary?.deductions || 0,
    status: salary?.status || "unpaid",
  });

  const finalSalary = Math.max(0, Number(data.baseSalary) - Number(data.deductions));

  const onChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    try {
      const payload = {
        month: data.month,
        baseSalary: Number(data.baseSalary),
        deductions: Number(data.deductions),
        finalSalary,
        status: data.status,
      };
      const response = await api.updateSalary(salary._id, payload);
      if (response?._id) {
        notify.success("Salary updated successfully!");
        getData();
        handleClose();
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong");
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <div className="taskForm">
        <div className="closeIcon" onClick={handleClose}>
          <i className="fa-solid fa-xmark" />
        </div>
        <div className="h3">Edit Salary</div>
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <label>Month</label>
              <input type="month" name="month" className="form-control" value={data.month} onChange={onChange} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Base Salary (Rs.)</label>
              <input type="number" name="baseSalary" className="form-control" value={data.baseSalary} onChange={onChange} min={0} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Deductions (Rs.)</label>
              <input type="number" name="deductions" className="form-control" value={data.deductions} onChange={onChange} min={0} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Final Salary (Rs.)</label>
              <input type="text" className="form-control" value={`Rs. ${finalSalary.toLocaleString()}`} readOnly style={{ background: "#f0fdf4", fontWeight: 700, color: "#065f46" }} />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Status</label>
              <select name="status" className="form-control" value={data.status} onChange={onChange}>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button className="themeBtn w-100 text-center" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </Modal>
  );
};

/* ─── Salary Slip Print Styles ─── */
const SALARY_PRINT_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; background: #fff; color: #222; }
  .receipt-wrapper { width: 700px; margin: 30px auto; padding: 40px; border: 2px solid #10b981; border-radius: 12px; }
  .receipt-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #10b981; padding-bottom: 20px; margin-bottom: 24px; }
  .receipt-header img { height: 60px; }
  .receipt-header-right { text-align: right; }
  .receipt-header-right h2 { color: #10b981; font-size: 22px; margin-bottom: 4px; }
  .receipt-header-right p { font-size: 12px; color: #666; }
  .receipt-title { text-align: center; font-size: 20px; font-weight: bold; letter-spacing: 2px; color: #10b981; margin-bottom: 24px; text-transform: uppercase; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 32px; margin-bottom: 24px; }
  .info-item label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 1px; display: block; margin-bottom: 2px; }
  .info-item span { font-size: 14px; font-weight: 600; color: #222; }
  .section-title { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #10b981; margin-bottom: 10px; }
  .summary-box { background: #f0fdf4; border: 1px solid #10b981; border-radius: 8px; padding: 16px 20px; margin-bottom: 24px; }
  .summary-row { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px; }
  .summary-row:last-child { margin-bottom: 0; border-top: 1px dashed #10b981; padding-top: 8px; font-weight: bold; font-size: 15px; }
  .status-badge { display: inline-block; padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: bold; text-transform: uppercase; }
  .status-paid   { background: #d1fae5; color: #065f46; }
  .status-unpaid { background: #fee2e2; color: #991b1b; }
  .receipt-footer { text-align: center; font-size: 11px; color: #aaa; border-top: 1px solid #e5e7eb; padding-top: 16px; margin-top: 8px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

/* ─── Salary Receipt Modal ─── */
const SalaryReceiptModal = ({ show, handleClose, salary }) => {
  const receiptRef = useRef();
  const slipNo = `SAL-${salary._id.toString().slice(-6).toUpperCase()}`;
  const statusClass =
    salary?.status === "paid" ? "status-paid" : "status-unpaid";

  const handlePrint = () => {
    const w = window.open("", "_blank", "width=800,height=600");
    w.document.write(
      `<!DOCTYPE html><html><head><title>Salary Slip ${slipNo}</title><style>${SALARY_PRINT_STYLES}</style></head><body>${receiptRef.current.innerHTML}</body></html>`,
    );
    w.document.close();
    w.focus();
    setTimeout(() => {
      w.print();
      w.close();
    }, 300);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <div className="taskForm" style={{ maxWidth: "100%" }}>
        <div className="closeIcon" onClick={handleClose}>
          <i className="fa-solid fa-xmark" />
        </div>

        <div className="d-flex justify-content-end gap-2 mb-3">
          <button className="btn btn-success" onClick={handlePrint}>
            <i className="fa-solid fa-print me-2"></i>Print / Save PDF
          </button>
        </div>

        <div ref={receiptRef}>
          <div className="receipt-wrapper">
            {/* Header */}
            <div className="receipt-header">
              <img src={logo} alt="Logo" style={{ height: 60 }} />
              <div className="receipt-header-right">
                <h2 style={{ color: "#10b981", marginBottom: 4 }}>
                  Shabbir Transport
                </h2>
                <p style={{ fontSize: 12, color: "#666" }}>
                  Slip No: <strong>{slipNo}</strong>
                </p>
                <p style={{ fontSize: 12, color: "#666" }}>
                  Printed: {moment().format("DD MMM YYYY, hh:mm A")}
                </p>
              </div>
            </div>

            <div className="receipt-title">Salary Slip</div>

            {/* Info */}
            <div className="info-grid">
              <div className="info-item">
                <label>Employee</label>
                <span>{salary?.driverId?.name || "—"}</span>
              </div>
              <div className="info-item">
                <label>Month</label>
                <span>{salary?.month || "—"}</span>
              </div>
              <div className="info-item">
                <label>Status</label>
                <span className={`status-badge ${statusClass}`}>
                  {salary?.status === "paid" ? "Paid" : "Unpaid"}
                </span>
              </div>
              <div className="info-item">
                <label>Generated On</label>
                <span>{moment(salary?.createdAt).format("DD MMM YYYY")}</span>
              </div>
            </div>

            {/* Breakdown */}
            <div className="section-title">Salary Breakdown</div>
            <div className="summary-box">
              <div className="summary-row">
                <span>Base Salary</span>
                <span>PKR {salary?.baseSalary?.toLocaleString()}</span>
              </div>
              <div className="summary-row">
                <span>Loan Deduction</span>
                <span
                  style={{ color: salary?.deductions > 0 ? "#dc2626" : "#222" }}
                >
                  - PKR {(salary?.deductions || 0)?.toLocaleString()}
                </span>
              </div>
              <div className="summary-row">
                <span>Net Payable</span>
                <span style={{ color: "#10b981" }}>
                  PKR {salary?.finalSalary?.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="receipt-footer">
              <p>
                This is a computer-generated salary slip. No signature required.
              </p>
              <p style={{ marginTop: 4 }}>
                Shabbir Transport &mdash;{" "}
                {moment(salary?.month, "YYYY-MM").format("MMMM YYYY")} Salary
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
